import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { Combobox } from '@headlessui/react';
import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { EmptyStateExtraSmall } from '@components/empty-state/empty-state';
import { IconProps } from '@components/icons';
import { IconArrowDown } from '@components/icons/arrow-down';
import { IconArrowUp } from '@components/icons/arrow-up';
import { IconDocument } from '@components/icons/document';
import { IconForm } from '@components/icons/form';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconList } from '@components/icons/list';
import { IconReturn } from '@components/icons/return';
import { Integration } from '@components/integrations/integrations-types';
import { Modal } from '@components/modal';
import { forceHideModal, hideModal, showModal } from '@components/modal/modal';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Keys } from '@components/types/keyboard';
import { useKeyCombo } from '@hooks/use-key-combo';
import { useScopedTranslate } from '@locale/translate';
import { Color, scrollbarStyle, theme } from '@styles/theme';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { minutes } from '@utils/milliseconds';
import { isMac } from '@utils/operating-system';
import { XOR } from '@utils/types';

import { useAIAgents } from 'api/ai-agents/hooks';
import { AIAgent } from 'api/ai-agents/requests';
import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { useCompanyTravellers } from 'api/company-traveller/hooks';
import { CompanyTraveller } from 'api/company-traveller/requests';
import { useSearch } from 'api/search/hooks';
import { SearchResponse } from 'api/search/requests';
import { Workspace } from 'api/workspaces/requests';
import { useWorkspaceMap } from 'ui/authenticated/main/helpers';
import { routePaths, routes } from 'ui/authenticated/navigation';
import { renderIntegrationIcon } from 'ui/authenticated/settings/pages/integrations/list';
import { useListSidebar } from 'ui/authenticated/settings/pages/organization/lists/sidebar';
import { useUserDetailsSidebar } from 'ui/authenticated/settings/pages/organization/user-details-sidebar';

import { useAgentDetailsSidebar } from '../ui/authenticated/main/pages/ai-agents/agent-details-sidebar';
import { getAgentIcon } from '../ui/authenticated/main/pages/ai-agents/list';
import { useCompanyTravellerProfileSidebar } from '../ui/authenticated/main/pages/company-travellers/sidebars/profile';

const shortcutKey = isMac ? Keys.Meta : Keys.Control;

const staleTime = minutes(10);

type Page = (typeof routes)[number] & {
  shortId: string;
  commandPaletteName: string;
  commandPaletteIcon: (props: IconProps) => JSX.Element;
};

type SearchData = SearchResponse & {
  travellers: Array<CompanyTraveller>;
  aiAgents: Array<AIAgent>;
  pages: Array<Page>;
};

type SearchDataKey = keyof SearchData;

const commandPaletteContext = createContext({
  showCommandPalette: noop,
  workspaceMap: {} as Record<string, Workspace>,
});

type CommandPaletteProviderProps = {
  isActive: boolean;
};

export const CommandPaletteProvider: React.FC<CommandPaletteProviderProps> = ({
  children,
  isActive,
}) => {
  const workspaceMap = useWorkspaceMap();
  const showCommandPalette = () => {
    if (!isActive) return;
    showModal({
      component: CommandPaletteModal,
      options: { variant: 'LARGE' },
    });
  };
  useKeyCombo({ keys: [shortcutKey, Keys.K], handler: showCommandPalette });
  return (
    <commandPaletteContext.Provider value={{ showCommandPalette, workspaceMap }}>
      {children}
    </commandPaletteContext.Provider>
  );
};

export const useCommandPalette = () => {
  const { showCommandPalette } = useContext(commandPaletteContext);
  return showCommandPalette;
};

const useTravellers = (searchString: string) => {
  const travellersQuery = useCompanyTravellers({ staleTime, keepPreviousData: true });
  const travellers =
    travellersQuery.data?.filter(
      (traveller) =>
        traveller.phone?.includes(searchString) ||
        (traveller.name ?? '').toLowerCase().includes(searchString) ||
        traveller.email.toLowerCase().includes(searchString)
    ) ?? [];
  return {
    travellers,
    isTravellersLoaded: !travellersQuery.isInitialLoading,
  };
};

const useAgents = (searchString: string) => {
  const agentsQuery = useAIAgents({ staleTime, keepPreviousData: true });
  const agents =
    agentsQuery.data?.filter((agent) => agent.name?.toLowerCase().includes(searchString)) ?? [];
  return { agents, isAiAgentsLoaded: !agentsQuery.isInitialLoading };
};

const usePages = (searchString: string) => {
  const palettePages = useRef(
    routes
      .filter((route) => route.hasOwnProperty('commandPaletteName'))
      .map((route) => ({ ...route, shortId: route.name })) as Array<Page>
  ).current;
  return palettePages.filter((page) =>
    page.commandPaletteName.toLowerCase().includes(searchString)
  );
};

const useData = (searchString: string) => {
  const [dataState, setDataState] = useState<SearchData | undefined>(undefined);
  const loweredString = searchString.toLowerCase();
  const pages = usePages(loweredString);
  const { travellers, isTravellersLoaded } = useTravellers(loweredString);
  const { agents: aiAgents, isAiAgentsLoaded } = useAgents(loweredString);

  useEffect(() => {
    if (!searchString) {
      setDataState(undefined);
    }
  }, [searchString]);

  useSearch(searchString, {
    enabled: Boolean(searchString) && isTravellersLoaded && isAiAgentsLoaded,
    onSuccess: (data) => {
      const users = data.users.filter((user) => user.lastLogin);
      return setDataState({ ...data, users, travellers, pages, aiAgents });
    },
  });

  return dataState;
};

const useHandleSelect = () => {
  const showUserDetailsSidebar = useUserDetailsSidebar();
  const showAgentDetailsSidebar = useAgentDetailsSidebar();
  const navigate = useNavigate();
  const showTravellerProfileSidebar = useCompanyTravellerProfileSidebar();
  const showListSidebar = useListSidebar();
  const companyTravellerAttributeTemplates = useCompanyTravellerAttributeTemplates({
    staleTime,
    keepPreviousData: true,
  });

  return (item: any & { category: SearchDataKey }) => {
    forceHideModal();
    if (item.category === 'travellers') {
      showTravellerProfileSidebar({
        companyTravellerShortId: item.id,
        companyTravellerAttributeTemplates: companyTravellerAttributeTemplates.data ?? [],
      });
    } else if (item.category === 'aiAgents') {
      showAgentDetailsSidebar(item);
    } else if (item.category === 'documenttemplates') {
      navigate(
        generatePath(routePaths.worksspaceDocumentDetail, {
          workspaceId: item.workspace,
          documentId: item.shortId,
          '*': '',
        })
      );
    } else if (item.category === 'journeys') {
      navigate(
        generatePath(routePaths.journeyDetails, {
          workspaceId: item.workspace.shortId,
          journeyId: item.shortId,
          '*': '',
        })
      );
    } else if (item.category === 'formtemplates') {
      navigate(
        generatePath(routePaths.workspaceFormTemplatesEdit, {
          workspaceId: item.workspace,
          shortId: item.shortId,
          '*': '',
        })
      );
    } else if (item.category === 'integrations') {
      navigate(
        generatePath(routePaths.integrationDetail, {
          integrationId: item.shortId,
          '*': '',
        })
      );
    } else if (item.category === 'lists') {
      showListSidebar({ listId: item.shortId });
    } else if (item.category === 'users') {
      showUserDetailsSidebar({ user: item });
    } else if (item.category === 'pages') {
      navigate(generatePath(routePaths[item.name as keyof typeof routePaths]));
    }
  };
};

const CommandPaletteModal = () => {
  const { t } = useScopedTranslate(locale);
  const [searchString, setSearchString] = useState('');
  const data = useData(searchString);
  const handleSelect = useHandleSelect();
  const hasNoResults = data && Object.values(data).every((items) => items.length === 0);

  return (
    <Modal>
      <StyledModalContent>
        <Stack style={{ overflow: 'hidden' }}>
          <Combobox onChange={handleSelect}>
            <div style={{ marginBottom: data ? pxToRem(16) : 0 }}>
              <Combobox.Input
                as={SearchBox}
                // @ts-ignore-next-line
                onChange={(value) => setSearchString(value.trim())}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    hideModal();
                  }
                }}
                onChangeDebounce={500}
                placeholder={t('searchPlaceholder')}
                autoComplete="off"
                autoFocus
              />
            </div>
            <div style={{ overflowY: 'auto', marginBottom: !hasNoResults ? pxToRem(16) : 0 }}>
              <Items
                data={data}
                category="journeys"
                titleProperty="title"
                iconComponent={IconJourneyItem}
              />
              <Items
                data={data}
                category="pages"
                titleProperty="commandPaletteName"
                getIconComponent={(page) => page.commandPaletteIcon}
              />
              <Items
                data={data}
                category="travellers"
                titleProperty="name"
                renderIcon={(traveller) => (
                  <Avatar size="md" name={traveller.name} src={traveller.picture} />
                )}
              />
              <Items
                data={data}
                category="integrations"
                titleProperty="name"
                renderIcon={(integration) => renderIntegrationIcon(integration as Integration)}
              />
              <Items
                data={data}
                category="aiAgents"
                titleProperty="name"
                renderIcon={(agent) => getAgentIcon(agent, 24)}
              />
              <Items
                data={data}
                category="documenttemplates"
                titleProperty="name"
                iconComponent={IconDocument}
              />
              <Items
                data={data}
                category="formtemplates"
                titleProperty="name"
                iconComponent={IconForm}
              />
              <Items data={data} category="lists" titleProperty="name" iconComponent={IconList} />
              <Items
                data={data}
                category="users"
                titleProperty="name"
                renderIcon={(user) => <Avatar size="md" name={user.name} src={user.picture} />}
              />
            </div>
          </Combobox>
        </Stack>
        {hasNoResults ? (
          <Stack space={6}>
            <EmptyStateExtraSmall description={t('noSearchResults')} />
            <div>
              {['checkForTypos', 'useGenericSearchTerm'].map((key) => (
                <Text
                  key={key}
                  text={`• ${t(key as keyof (typeof locale)['en'])}`}
                  font="regular14"
                />
              ))}
            </div>
          </Stack>
        ) : (
          <KeyboardShortcuts />
        )}
      </StyledModalContent>
    </Modal>
  );
};

type ItemsProps<K extends SearchDataKey> = {
  data?: SearchData;
  category: K;
  titleProperty: keyof SearchData[K][number];
} & XOR<
  XOR<
    { iconComponent: (props: IconProps) => JSX.Element },
    { renderIcon: (item: SearchData[K][number]) => ReactNode }
  >,
  { getIconComponent: (item: SearchData[K][number]) => (props: IconProps) => JSX.Element }
>;

const Items = <K extends SearchDataKey>({
  data,
  category,
  titleProperty,
  iconComponent,
  renderIcon,
  getIconComponent,
}: ItemsProps<K>) => {
  const { t } = useScopedTranslate(locale);
  const items = data?.[category];
  const { workspaceMap } = useContext(commandPaletteContext);
  const getWorkspaceTitle = (item: SearchData[K][number]) => {
    if ('workspace' in item && item.workspace) {
      if (typeof item.workspace === 'string') {
        return workspaceMap[item.workspace]?.title;
      }
      return (item.workspace as Workspace).title;
    }
  };

  if (!items?.length) return null;

  return (
    <div>
      <Text text={t(category)} font="semibold14" style={{ padding: pxToRem(8) }} />
      <div>
        {items.map((item) => (
          <OptionItem key={item.shortId} value={{ ...item, category }}>
            {({ active }) => {
              const Icon = iconComponent ?? getIconComponent?.(item);
              const workspaceTitle = getWorkspaceTitle(item);
              return (
                <OptionInner $active={active}>
                  <Stack
                    orientation="horizontal"
                    justify="space-between"
                    align="center"
                    style={{ flex: 1 }}
                  >
                    <Stack orientation="horizontal" space={2} align="center">
                      {renderIcon?.(item)}
                      {Icon && <Icon size={24} color="elementPurple80" />}
                      <Stack orientation="horizontal" space={2} align="center">
                        <Text text={item[titleProperty as keyof typeof item]} font="regular14" />
                        {workspaceTitle && (
                          <Text
                            text={`• ${t('inWorkspace', { workspaceTitle })}`}
                            font="regular14"
                            color="textHelper"
                          />
                        )}
                      </Stack>
                    </Stack>
                    {active && (
                      <KeyboardIconContainer $size={24}>
                        <IconReturn {...iconProps} />
                      </KeyboardIconContainer>
                    )}
                  </Stack>
                </OptionInner>
              );
            }}
          </OptionItem>
        ))}
      </div>
    </div>
  );
};

const StyledModalContent = styled(Modal.Content)({
  display: 'flex',
  flexDirection: 'column',
  ...scrollbarStyle,
});

const OptionItem = styled(Combobox.Option)({
  cursor: 'pointer',
  listStyle: 'none',
});

const OptionInner = styled.div<{ $active: boolean }>(({ $active }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: pxToRem(8),
  borderRadius: pxToRem(8),
  backgroundColor: $active ? theme.color.uiTrans5 : 'transparent',
}));

type KeyboardShortcutProps = {
  icons: Array<ReactNode>;
  text: string;
};

const KeyboardShortcut = ({ icons, text }: KeyboardShortcutProps) => {
  return (
    <Stack
      space={2}
      orientation="horizontal"
      align="center"
      style={{
        backgroundColor: theme.color.uiTrans5,
        padding: pxToRem(8),
        borderRadius: pxToRem(4),
      }}
    >
      <Stack space={2} orientation="horizontal">
        {icons.map((icon, index) => (
          <KeyboardIconContainer key={index}>{icon}</KeyboardIconContainer>
        ))}
      </Stack>
      <Text text={text} font="semibold14" color="textSecondary" />
    </Stack>
  );
};

const iconProps = { size: 16, color: 'elementPurple80' as Color };

const KeyboardIconContainer = styled.div<{ $size?: number }>(({ $size = 32 }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.color.backgroundLightDark,
  padding: pxToRem(8),
  borderRadius: pxToRem(4),
  height: pxToRem($size),
  width: pxToRem($size),
  borderColor: theme.color.border10,
  borderWidth: pxToRem(1),
  borderStyle: 'solid',
}));

const KeyboardShortcuts = () => {
  const { t } = useScopedTranslate(locale);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        flexGrow: 0,
        flexShrink: 0,
      }}
    >
      <Stack space={2} orientation="horizontal" align="center">
        <KeyboardShortcut icons={[<IconReturn {...iconProps} />]} text={t('toSelect')} />
        <KeyboardShortcut
          icons={[<IconArrowUp {...iconProps} />, <IconArrowDown {...iconProps} />]}
          text={t('toNavigate')}
        />
        <KeyboardShortcut
          icons={[<Text color={iconProps.color} text="ESC" font="semibold12" />]}
          text={t('toClose')}
        />
      </Stack>
    </div>
  );
};

const locale = {
  en: {
    journeys: 'Journeys',
    formtemplates: 'Forms',
    users: 'Users',
    documenttemplates: 'Documents',
    travellers: 'Travellers',
    pages: 'Pages',
    integrations: 'Integrations',
    lists: 'Lists',
    aiAgents: 'AI Agents',
    toSelect: 'To select',
    toNavigate: 'To navigate',
    toClose: 'To close',
    searchPlaceholder: 'Search for and navigate...',
    noSearchResults: 'No search results found',
    checkForTypos: 'Check your search for typos',
    useGenericSearchTerm: 'Use a more generic search term',
    inWorkspace: 'in {workspaceTitle}',
  },
};
